/**
 * Game Engine - Main Entry Point
 *
 * Coordinates all engine systems:
 * - Game loop (fixed timestep physics)
 * - Renderer (Three.js)
 * - Physics (Cannon-ES)
 * - Audio (Howler)
 * - Input (Touch/Pointer)
 * - Events
 */

import { GameLoop, GameLoopConfig } from './core/GameLoop';
import { EventEmitter, AllEvents } from './core/EventEmitter';
import { Renderer, RendererConfig, RenderQuality } from './render/Renderer';
import { PhysicsWorld, PhysicsConfig } from './physics/PhysicsWorld';
import { AudioManager } from './audio/AudioManager';
import { InputManager, GestureConfig } from './input/InputManager';

export interface EngineConfig {
  container: HTMLElement;
  renderer?: Partial<RendererConfig>;
  physics?: PhysicsConfig;
  loop?: GameLoopConfig;
  input?: GestureConfig;
}

export class Engine extends EventEmitter<AllEvents> {
  // Core systems
  readonly renderer: Renderer;
  readonly physics: PhysicsWorld;
  readonly audio: AudioManager;
  readonly input: InputManager;

  // Game loop
  private gameLoop: GameLoop;
  private isRunning = false;

  // Update callbacks
  private fixedUpdateCallbacks: Set<(dt: number) => void> = new Set();
  private updateCallbacks: Set<(dt: number) => void> = new Set();
  private renderCallbacks: Set<(interpolation: number) => void> = new Set();

  constructor(config: EngineConfig) {
    super();

    // Initialize renderer
    this.renderer = new Renderer({
      container: config.container,
      ...config.renderer,
    });

    // Initialize physics
    this.physics = new PhysicsWorld(config.physics);

    // Initialize audio
    this.audio = new AudioManager();

    // Initialize input
    this.input = new InputManager(this.renderer.canvas, config.input);

    // Setup input event forwarding
    this.setupInputForwarding();

    // Initialize game loop
    this.gameLoop = new GameLoop(
      {
        fixedUpdate: this.fixedUpdate.bind(this),
        update: this.update.bind(this),
        render: this.render.bind(this),
      },
      config.loop
    );
  }

  private setupInputForwarding(): void {
    // Forward input events to engine event system
    this.input.on('input:touch_start', (data) => this.emit('input:touch_start', data));
    this.input.on('input:touch_move', (data) => this.emit('input:touch_move', data));
    this.input.on('input:touch_end', (data) => this.emit('input:touch_end', data));
    this.input.on('input:pointer_down', (data) => this.emit('input:pointer_down', data));
    this.input.on('input:pointer_move', (data) => this.emit('input:pointer_move', data));
    this.input.on('input:pointer_up', (data) => this.emit('input:pointer_up', data));
  }

  /**
   * Fixed timestep update (physics)
   * Stores previous state before stepping for interpolation
   */
  /** The fixed physics step (s). Pre-run simulation must use this exact dt. */
  get fixedDelta(): number {
    return this.gameLoop.fixedDelta;
  }

  private fixedUpdate(deltaTime: number): void {
    // Store previous state for interpolation before stepping
    this.physics.storePreviousState();

    // Step physics
    this.physics.step(deltaTime);

    // Call registered fixed update callbacks
    for (const callback of this.fixedUpdateCallbacks) {
      callback(deltaTime);
    }
  }

  /**
   * Variable update (game logic)
   */
  private update(deltaTime: number): void {
    // Call registered update callbacks
    for (const callback of this.updateCallbacks) {
      callback(deltaTime);
    }
  }

  /**
   * Render
   */
  private render(interpolation: number): void {
    // Interpolate physics bodies for smooth visuals
    this.physics.interpolate(interpolation);

    // Call registered render callbacks (sync visuals to physics)
    for (const callback of this.renderCallbacks) {
      callback(interpolation);
    }

    // Render the scene
    this.renderer.render();
  }

  /**
   * Start the engine
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.gameLoop.start();
    this.emit('engine:start', undefined);
  }

  /**
   * Stop the engine
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;
    this.gameLoop.stop();
    this.emit('engine:stop', undefined);
  }

  /**
   * Pause the engine (keeps rendering, stops updates)
   */
  pause(): void {
    // TODO: Implement pause state
    this.emit('engine:pause', undefined);
  }

  /**
   * Resume the engine
   */
  resume(): void {
    // TODO: Implement resume
    this.emit('engine:resume', undefined);
  }

  /**
   * Register a fixed update callback (physics rate)
   */
  onFixedUpdate(callback: (deltaTime: number) => void): () => void {
    this.fixedUpdateCallbacks.add(callback);
    return () => this.fixedUpdateCallbacks.delete(callback);
  }

  /**
   * Register a variable update callback (every frame)
   */
  onUpdate(callback: (deltaTime: number) => void): () => void {
    this.updateCallbacks.add(callback);
    return () => this.updateCallbacks.delete(callback);
  }

  /**
   * Register a render callback (every frame, after physics interpolation)
   */
  onRender(callback: (interpolation: number) => void): () => void {
    this.renderCallbacks.add(callback);
    return () => this.renderCallbacks.delete(callback);
  }

  /**
   * Get performance stats
   */
  getStats() {
    return {
      loop: this.gameLoop.getStats(),
      render: this.renderer.getStats(),
    };
  }

  /**
   * Set render quality
   */
  setQuality(quality: RenderQuality): void {
    this.renderer.setQuality(quality);
  }

  /**
   * Check if engine is running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get Three.js scene
   */
  get scene() {
    return this.renderer.scene;
  }

  /**
   * Get Three.js camera
   */
  get camera() {
    return this.renderer.camera;
  }

  /**
   * Get canvas element
   */
  get canvas() {
    return this.renderer.canvas;
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    this.stop();

    this.input.dispose();
    this.audio.dispose();
    this.physics.dispose();
    this.renderer.dispose();

    this.fixedUpdateCallbacks.clear();
    this.updateCallbacks.clear();
    this.renderCallbacks.clear();
    this.removeAllListeners();
  }
}

// Export all engine modules
export * from './core';
export * from './render/Renderer';
export * from './physics/PhysicsWorld';
export * from './audio/AudioManager';
export * from './input/InputManager';
